import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { cn } from './cn.js';
import { IconButton } from './icon-button.js';

const tools = [
  { command: 'bold', label: 'Negrito', icon: Bold },
  { command: 'italic', label: 'Itálico', icon: Italic },
  { command: 'underline', label: 'Sublinhado', icon: Underline },
  { command: 'insertUnorderedList', label: 'Lista com marcadores', icon: List },
  { command: 'insertOrderedList', label: 'Lista numerada', icon: ListOrdered },
] as const;

export interface RichTextEditorProps {
  label: string;
  /// HTML. Quem grava saneia: o editor só escreve o que o navegador produz.
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

/// Texto com negrito, itálico, sublinhado e listas — nada além, porque é o que a lista fechada de
/// quem grava aceita. A barra usa o comando de edição do navegador para o cursor e o desfazer se
/// comportarem como em qualquer editor; o conteúdo só é reescrito quando o valor vem de fora, senão
/// o cursor pularia para o começo a cada tecla.
export function RichTextEditor({
  label,
  value,
  onValueChange,
  placeholder,
  error,
  hint,
  id: providedId,
  className,
}: RichTextEditorProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const labelId = `${id}-label`;
  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const editor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = editor.current;
    if (element && element.innerHTML !== value) element.innerHTML = value;
  }, [value]);

  function run(command: (typeof tools)[number]['command']) {
    const element = editor.current!;
    element.focus();
    document.execCommand(command);
    onValueChange(element.innerHTML);
  }

  return (
    <div className={cn('space-y-2', className)}>
      <span id={labelId} className="block text-xs font-semibold text-content/70">
        {label}
      </span>
      <div
        className={cn(
          'overflow-hidden rounded-xl border bg-surface-elevated transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20',
          error ? 'border-danger/70' : 'border-surface-border',
        )}
      >
        <div
          role="toolbar"
          aria-label={`Formatação de ${label}`}
          className="flex flex-wrap gap-0.5 border-b border-surface-border px-1.5 py-1"
        >
          {tools.map((tool) => (
            <IconButton
              key={tool.command}
              label={tool.label}
              icon={<tool.icon className="size-4" />}
              // Sem isto o clique tira o foco do texto e o comando não acha a seleção.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => run(tool.command)}
            />
          ))}
        </div>
        <div
          ref={editor}
          id={id}
          role="textbox"
          tabIndex={0}
          aria-multiline="true"
          aria-labelledby={labelId}
          aria-invalid={Boolean(error)}
          aria-describedby={descriptionId}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={(event) => onValueChange(event.currentTarget.innerHTML)}
          className="hue-rich-text min-h-28 cursor-text px-4 py-3 text-sm leading-6 text-content outline-none empty:before:text-content-faint empty:before:content-[attr(data-placeholder)]"
        />
      </div>
      {error ? (
        <p id={descriptionId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
